import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFgp } from "@/context/FgpContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Lock, Mail, ArrowRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

export default function PortalLogin() {
  const { loginAsOwner } = useFgp();
  const navigate = useNavigate();
  const [f, setF] = useState({ email: "mahmoud.badawi.mb@gmail.com", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.email.trim() || !f.password.trim()) return toast.error("Enter owner email and password");
    setLoading(true);
    try {
      await loginAsOwner(f.email.trim(), f.password);
      toast.success("Signed in as Platform Owner");
      navigate("/app/admin");
    } catch (err) {
      toast.error(err.message || "Access denied");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-900 text-white flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-fade opacity-[0.12]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[360px] w-[640px] bg-[#516670]/30 blur-[120px] rounded-full" />
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          <span className="h-11 w-11 rounded-xl bg-[#516670] flex items-center justify-center"><Shield className="h-5 w-5" /></span>
          <div>
            <p className="font-heading text-lg font-semibold leading-tight">Platform Portal</p>
            <p className="text-xs text-neutral-400">Owner & administration access</p>
          </div>
        </div>

        <h1 className="font-heading text-2xl font-semibold">Sign in to the control room</h1>
        <p className="mt-1 text-sm text-neutral-400">Restricted to the platform owner. Oversee every workspace and account.</p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="portal-form">
          <div className="space-y-1.5">
            <Label className="text-neutral-300">Owner email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
              <Input value={f.email} onChange={set("email")} className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-neutral-500" data-testid="portal-email" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-neutral-300">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
              <Input type="password" value={f.password} onChange={set("password")} placeholder="••••••••" className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-neutral-500" data-testid="portal-password" />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#516670] hover:bg-[#41545d] gap-2" data-testid="portal-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Enter control room <ArrowRight className="h-4 w-4" /></>}
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-neutral-500">This portal is independent of any organization or workspace.</p>
      </motion.div>
    </div>
  );
}
